import React, { useEffect, useState, useRef } from "react";
import { TextWrapper, Wrapper, TextWrapperSpan } from "../../../constants/common/StyledGlobalConstants";
import ContactRadioCard from "./radioCard/ContactRadioCard";
import ContactDetailsFormValidation from "./contactDetailsForm/ContactDetailsFormValidation";
import AddressDetailsForm from "./contactDetailsForm/AddressDetailsForm";
import { LabelContainer } from "../../myContract/ContactMe.styles";
import { CustomisedTextarea } from "../../myContract/RequestDetails.styles";
import "./ContactOverview.css";
import { useSelector, useDispatch } from "react-redux";
import {
  userSelectedAddressOption,
  userSelectedContactOption,
  setNotesToTechnician,
  setIsContactFormError,
} from "../../../store/actions/schedulingportal/technicianAppointment";
import { DEVICE_BREAKPOINTS } from "../../../constants/appConstants";
import { EmailRegex } from "../../../util/regexUtils";
import { PhoneNumberUtil } from "google-libphonenumber";
import PropTypes from "prop-types";
import { getPendoIdPrefix } from "../../../util/cfstPendoUtils";

const { TABLET } = DEVICE_BREAKPOINTS;

const ContactOverview = (props) => {
  const {
    userSelectedContactOption: selectedContactOption,
    userSelectedAddressOption: selectedAddressOption,
    notesToTechnician,
  } = useSelector((state) => state.TECHNICIANAPPOINTMENT);
  const { windowSize, firstName, surname, emailAddress, phoneNumber } = useSelector((state) => state.ACCOUNT);

  const radioContent = [
    {
      name: "contact",
      label: "Use my Reveal contact",
      value: "use-existing-contact",
      ariaLabel: "Use my Reveal contact",
      inputId: `${getPendoIdPrefix()}-contact-details-page-use-existing-contact-radio-button`,
      disabled: false,
    },
    {
      name: "contact",
      label: "Add new contact",
      value: "add-new-contact",
      ariaLabel: "Add new contact",
      inputId: `${getPendoIdPrefix()}-contact-details-page-add-new-contact-radio-button`,
      disabled: false,
    },
  ];

  const radioAddress = [
    {
      name: "addressDetails",
      label: "Use existing address",
      value: "use-existing-address",
      ariaLabel: "Use existing address",
      inputId: `${getPendoIdPrefix()}-contact-details-page-use-existing-address-radio-button`,
      disabled: false,
    },
    {
      name: "addressDetails",
      label: "Add new address",
      value: "add-new-address",
      ariaLabel: "Add new address",
      inputId: `${getPendoIdPrefix()}-contact-details-page-add-new-address-radio-button`,
      disabled: false,
    },
  ];

  const [contactRadioButtonData, setContactRadioButtonData] = useState(radioContent);
  const [addressRadioButtonData, setAddressRadioButtonData] = useState(radioAddress);
  const [contactFormSuccess, setContactFormSuccess] = useState(false);
  const [addressFormSuccess, setAddressFormSuccess] = useState(false);
  const [contactFormError, setContactFormError] = useState(false);
  const [showContactFormBanner, setShowContactFormBanner] = useState(false);
  const [existingContactFormSuccess, setExistingContactFormSuccess] = useState(false);
  const [existingContactFormError, setExistingContactFormError] = useState(false);
  const phoneUtil = PhoneNumberUtil.getInstance();
  const dispatch = useDispatch();
  const bannerRef = useRef(null);

  const validateExistingContactForm = () => {
    // Check if existing contact form has validation errors OR missing fields
    const hasMissingFields = !firstName || !surname || !emailAddress || !phoneNumber;

    // Check if any field has validation errors (invalid format)
    const hasValidationErrors =
      (firstName && (firstName.trim().toLowerCase() === "default" || firstName.trim().toLowerCase() === "admin")) ||
      (surname && surname.trim().toLowerCase() === "admin") ||
      (emailAddress && !EmailRegex.test(emailAddress)) ||
      (phoneNumber && !isPhoneValid(phoneNumber.replace(/\D/g, ""))); // Use the same isPhoneValid function

    console.log("validateExistingContactForm:", {
      hasMissingFields,
      hasValidationErrors,
      firstName,
      surname,
      emailAddress,
      phoneNumber,
      totalErrors: hasMissingFields || hasValidationErrors,
    });

    return hasMissingFields || hasValidationErrors;
  };

  const isPhoneValid = (phone) => {
    try {
      const phoneWithCountryCode = phone.startsWith("+1") ? phone : `+1${phone.replace(/\D/g, "")}`;
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phoneWithCountryCode));
    } catch (error) {
      console.log("Phone validation error:", error);
      return false;
    }
  };

  const handleContactSwitch = (name, value) => {
    if (value === "use-existing-contact" || value === "add-new-contact") {
      handleContactOptionSwitch(value);
    } else if (value === "use-existing-address" || value === "add-new-address") {
      handleAddressOptionSwitch(value);
    } else {
      clearRadioChildren();
    }
  };

  const handleContactOptionSwitch = (value) => {
    if (value === "use-existing-contact") {
      dispatch(userSelectedContactOption(value));
      dispatch(setIsContactFormError(false));
      const hasExistingContactErrors = validateExistingContactForm();
      if (hasExistingContactErrors) setShowContactFormBanner(true);
      radioContent[0].children = (
        <ContactDetailsFormValidation
          id="cfst-existing-contact-option"
          setContactFormSuccess={setExistingContactFormSuccess}
          setContactFormError={setExistingContactFormError}
          setShowContactFormBanner={setShowContactFormBanner}
          showContactFormBanner={hasExistingContactErrors ? true : showContactFormBanner}
          bannerRef={bannerRef}
        />
      );
      radioContent[1].children = "";
      setContactRadioButtonData([...radioContent]);
    } else if (value === "add-new-contact") {
      const hasExistingContactErrors = validateExistingContactForm();
      if (hasExistingContactErrors) {
        setShowContactFormBanner(true);
        radioContent[0].children = (
          <ContactDetailsFormValidation
            key="existing-contact-force-validation"
            id="cfst-existing-contact-option"
            setContactFormSuccess={setExistingContactFormSuccess}
            setContactFormError={setExistingContactFormError}
            setShowContactFormBanner={setShowContactFormBanner}
            forceValidation={true}
            userSelectedContactOption="use-existing-contact"
            showContactFormBanner={true}
            bannerRef={bannerRef}
          />
        );
        radioContent[1].children = (
          <ContactDetailsFormValidation
            key="new-contact-form"
            id="cfst-new-contact-option"
            setContactFormSuccess={setContactFormSuccess}
            setContactFormError={setContactFormError}
            setShowContactFormBanner={setShowContactFormBanner}
            userSelectedContactOption="add-new-contact"
            showContactFormBanner={false}
          />
        );
      } else {
        setShowContactFormBanner(false);
        setExistingContactFormSuccess(true);
        setExistingContactFormError(false);
        radioContent[0].children = "";
        radioContent[1].children = (
          <ContactDetailsFormValidation
            key="new-contact-only"
            id="cfst-new-contact-option"
            setContactFormSuccess={setContactFormSuccess}
            setContactFormError={setContactFormError}
            setShowContactFormBanner={setShowContactFormBanner}
            userSelectedContactOption="add-new-contact"
            showContactFormBanner={false}
          />
        );
      }
      dispatch(userSelectedContactOption(value));
      dispatch(setIsContactFormError(false));
      setContactRadioButtonData([...radioContent]);
    }
  };

  const handleAddressOptionSwitch = (value) => {
    if (contactFormError) dispatch(setIsContactFormError(true));
    if (value === "use-existing-address") {
      dispatch(userSelectedAddressOption(value));
      addressRadioButtonData[0].children = <AddressDetailsForm id="cfst-existing-address-option" setAddressFormSuccess={setAddressFormSuccess} />;
      addressRadioButtonData[1].children = "";
    } else if (value === "add-new-address") {
      setAddressFormSuccess(false);
      dispatch(userSelectedAddressOption(value));
      addressRadioButtonData[0].children = "";
      addressRadioButtonData[1].children = <AddressDetailsForm id="cfst-new-address-option" setAddressFormSuccess={setAddressFormSuccess} />;
    }
    setAddressRadioButtonData([...addressRadioButtonData]);
  };

  const clearRadioChildren = () => {
    radioContent[0].children = "";
    radioContent[1].children = "";
    addressRadioButtonData[0].children = "";
    addressRadioButtonData[1].children = "";
    setContactRadioButtonData([...radioContent]);
    setAddressRadioButtonData([...addressRadioButtonData]);
  };

  // Scroll to banner when it's shown
  useEffect(() => {
    if (showContactFormBanner && bannerRef.current) {
      bannerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [showContactFormBanner]);

  useEffect(() => {
    if (selectedContactOption) handleContactSwitch("", selectedContactOption);
    if (selectedAddressOption) handleContactSwitch("", selectedAddressOption);
  }, []);

  useEffect(() => {
    // Next button should be enabled only when:
    // 1. Contact form is successfully completed (all fields filled and valid)
    // 2. Address form is successfully completed (address option selected)
    // 3. Contact option is selected
    // 4. Address option is selected
    // 5. If existing contact form is visible (when switching to add-new-contact with errors), it must also be valid

    const isContactOptionSelected =
      selectedContactOption && (selectedContactOption === "use-existing-contact" || selectedContactOption === "add-new-contact");
    const isAddressOptionSelected =
      selectedAddressOption && (selectedAddressOption === "use-existing-address" || selectedAddressOption === "add-new-address");

    // Check if both forms are visible (when add-new-contact is selected but existing contact has errors)
    const bothFormsVisible = selectedContactOption === "add-new-contact" && showContactFormBanner;

    let isFormValid = false;

    if (selectedContactOption === "use-existing-contact") {
      // Only existing contact form should be valid
      isFormValid = existingContactFormSuccess && !existingContactFormError;
    } else if (selectedContactOption === "add-new-contact") {
      if (bothFormsVisible) {
        // Both forms must be valid when both are visible
        isFormValid = contactFormSuccess && !contactFormError && existingContactFormSuccess && !existingContactFormError;
      } else {
        // Only new contact form needs to be valid
        isFormValid = contactFormSuccess && !contactFormError;
      }
    }

    if (isFormValid && addressFormSuccess && isContactOptionSelected && isAddressOptionSelected) {
      props.setIsNextDisabled(false);
    } else {
      props.setIsNextDisabled(true);
    }

    console.log("Next button validation:", {
      contactFormSuccess,
      existingContactFormSuccess,
      contactFormError,
      existingContactFormError,
      addressFormSuccess,
      isContactOptionSelected,
      isAddressOptionSelected,
      bothFormsVisible,
      selectedContactOption,
      selectedAddressOption,
      showContactFormBanner,
      isFormValid,
      nextButtonEnabled: isFormValid && addressFormSuccess && isContactOptionSelected && isAddressOptionSelected,
    });
  }, [
    contactFormSuccess,
    existingContactFormSuccess,
    contactFormError,
    existingContactFormError,
    addressFormSuccess,
    selectedContactOption,
    selectedAddressOption,
    showContactFormBanner,
  ]);

  console.log("ContactOverview - showContactFormBanner:", showContactFormBanner);
  console.log("ContactOverview - Account data:", { firstName, surname, emailAddress, phoneNumber });

  return (
    <>
      <Wrapper display="flex" flexDirection="column" alignItems="flex-start">
        <TextWrapper fontFamily="neue-haas-grotesk-display" marginTop="24px !important" fontSize="2rem" fontWeight="700" lineHeight="2.5rem">
          Contact
        </TextWrapper>

        <TextWrapper marginTop={windowSize.width < TABLET ? "8px !important" : "1rem !important"}>
          <TextWrapperSpan aria-label="Select existing contact or choose " fontSize="14px" fontWeight="400" lineHeight="1.25rem">
            {"Select existing contact or choose "}
          </TextWrapperSpan>
          <TextWrapperSpan aria-label="Add new contact" fontSize="14px" fontWeight="700" lineHeight="1.25rem">
            {"Add new contact"}
          </TextWrapperSpan>
          <TextWrapperSpan aria-label=" for the technician to communicate with. " fontSize="14px" fontWeight="400" lineHeight="1.25rem">
            {" for the technician to communicate with. "}
          </TextWrapperSpan>
        </TextWrapper>

        <ContactRadioCard
          id="contact-radio-button"
          ariaLabel={`conatct form`}
          handleContactSwitch={handleContactSwitch}
          defaultValue={"test"}
          values={contactRadioButtonData}
        ></ContactRadioCard>
      </Wrapper>

      <Wrapper display="flex" flexDirection="column" alignItems="flex-start">
        <TextWrapper fontFamily="neue-haas-grotesk-display" marginTop="40px !important" fontSize="2rem" fontWeight="700" lineHeight="3rem">
          Appointment location
        </TextWrapper>
        <TextWrapper fontSize="0.875rem" fontWeight="400" lineHeight="1.25rem" marginTop="1rem !important">
          Select an existing address from the dropdown or choose <b> Add new address</b> to add a new one.
        </TextWrapper>

        <ContactRadioCard
          id="address-radio-button"
          ariaLabel={`address form`}
          handleContactSwitch={handleContactSwitch}
          defaultValue={"test"}
          values={addressRadioButtonData}
        ></ContactRadioCard>
      </Wrapper>
      <Wrapper display="flex" marginTop="32px" flexDirection="column" alignItems="flex-start">
        <LabelContainer>
          <label>{"Additional instructions for technician"}</label>
        </LabelContainer>

        <CustomisedTextarea
          className="cfstscroll"
          height="99px !important"
          placeholder="For example: detailed directions, parking information, gate codes, or who to contact on arrival."
          value={notesToTechnician}
          tabIndex={0}
          style={{ resize: "none" }}
          aria-label="Additional instructions for technician"
          maxLength={500}
          onChange={(e) => {
            dispatch(setNotesToTechnician(e.target.value));
          }}
        />
      </Wrapper>

      <TextWrapper style={{ fontSize: "12px", lineHeight: "18px", fontWeight: "400", color: "#747474", marginTop: "4px" }}>
        Do not add additional service requests here.
      </TextWrapper>
    </>
  );
};

ContactOverview.propTypes = {
  setIsNextDisabled: PropTypes.func.isRequired,
};

export default ContactOverview;
